import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Loader2, ArrowUpRight, Upload, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// Reusable "Buy" button — opens an optional "add your logo" step, then
// kicks off Stripe Checkout.
// Props: kind ('product' | 'set' | 'deposit'), targetId (code / slug / 'reservation'),
// label, variant ('gold' | 'outline'), testid, designUrl (pre-filled from an
// on-page customizer, e.g. LogoCustomizer on the set page).
export default function BuyButton({ kind, targetId, label = 'Buy', variant = 'gold', testid, priceLabel, designUrl }) {
  const [open, setOpen] = useState(false);
  const [localDesign, setLocalDesign] = useState(designUrl || null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) setLocalDesign(designUrl || null);
  }, [open, designUrl]);

  const uploadFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/customize/upload`, fd);
      setLocalDesign(data.url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Upload failed — please try again.');
    } finally {
      setUploading(false);
    }
  };

  const go = async (withDesign) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        kind, target_id: targetId, origin_url: window.location.origin,
        custom_design_url: withDesign ? (localDesign || null) : null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const cls = variant === 'outline' ? 'btn-outline-gold' : 'btn-gold';

  return (
    <div className="inline-flex flex-col items-start gap-2">
      <button
        className={`${cls} disabled:opacity-50`}
        onClick={() => setOpen(true)}
        data-testid={testid || `buy-${kind}-${targetId}`}
      >
        {label}{priceLabel ? ` · ${priceLabel}` : ''} <ArrowUpRight size={14} strokeWidth={1.25} />
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <DialogContent
          className="bg-mb-surface border-mb-border text-mb max-w-md"
          data-testid="buy-design-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-mb">
              Add your logo to this order
            </DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-mb-muted leading-relaxed -mt-2">
            Optional — drop your company logo and the atelier will use it to brand
            this order. You can always send it later instead.
          </p>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) uploadFile(f);
            }}
            onClick={() => fileRef.current?.click()}
            className={`mt-2 hairline border-dashed rounded-sm p-6 min-h-[120px] flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${dragOver ? 'border-gold bg-mb-surface-rich/40' : 'border-mb-border'}`}
            data-testid="buy-design-dropzone"
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) uploadFile(e.target.files[0]);
                e.target.value = '';
              }}
              data-testid="buy-design-file-input"
            />
            {uploading ? (
              <Loader2 size={20} className="animate-spin text-gold" />
            ) : localDesign ? (
              <>
                <img
                  src={`${BACKEND}${localDesign}`}
                  alt="Your logo"
                  className="h-14 object-contain"
                  data-testid="buy-design-preview-img"
                />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLocalDesign(null); }}
                  className="eyebrow-sm text-mb-muted hover:text-gold flex items-center gap-1 mt-1"
                  data-testid="buy-design-remove-btn"
                >
                  <X size={12} /> Remove
                </button>
              </>
            ) : (
              <>
                <Upload size={20} className="text-mb-muted" />
                <div className="eyebrow-sm text-mb-muted">Drop a logo, or click to browse</div>
              </>
            )}
          </div>

          {error && (
            <p className="text-[13px] text-red-400" data-testid="buy-design-error">{String(error)}</p>
          )}

          <DialogFooter className="mt-4 flex-row sm:justify-between gap-3 items-center">
            <button
              type="button"
              onClick={() => go(false)}
              disabled={busy || uploading}
              className="eyebrow-sm text-mb-muted hover:text-gold disabled:opacity-50"
              data-testid="buy-design-skip-btn"
            >
              Skip for now
            </button>
            <button
              type="button"
              onClick={() => go(true)}
              disabled={busy || uploading}
              className="btn-gold disabled:opacity-50"
              data-testid="buy-design-continue-btn"
            >
              {busy ? (
                <><Loader2 size={14} className="animate-spin" /> Redirecting…</>
              ) : (
                <>Continue to payment <ArrowUpRight size={14} strokeWidth={1.25} /></>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
